import React from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

/**
 * AdminMaintenanceButtons — 6 admin-only maintenance actions for the Planificator toolbar.
 * Extracted from Planificator.jsx (was lines ~620-777, ~158 lines of inline handlers).
 *
 * Props:
 *  - userRole: string — only renders for "admin"
 *  - onChanged: () => void — called after a successful action that mutates data, to reload
 */
export default function AdminMaintenanceButtons({ userRole, onChanged }) {
  if (userRole !== "admin") return null;

  const backfillSchedule = async () => {
    try {
      const dry = await api.post("/admin/backfill-schedule-propagation", { dry_run: true });
      const { extended, skipped_inactive, skipped_no_target, total_candidates } = dry.data || {};
      if (!extended) {
        toast.info(`Niciun entry de extins (${total_candidates} candidate, ${skipped_inactive} inactive, ${skipped_no_target} fără țintă)`);
        return;
      }
      if (!window.confirm(`Vor fi extinse ${extended} alocări single-day la durata completă a proiectului/regiei. Continui?`)) return;
      const res = await api.post("/admin/backfill-schedule-propagation", { dry_run: false });
      toast.success(`Backfill OK: ${res.data?.extended || 0} alocări extinse`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare backfill");
    }
  };

  const fixSaturday = async () => {
    try {
      const res = await api.post("/admin/fix-saturday-backfilled-entries");
      const { fixed, items } = res.data || {};
      if (!fixed) {
        toast.info("Nicio alocare sâmbăta de reparat");
        return;
      }
      toast.success(`✓ ${fixed} alocări de sâmbătă reparate (revenite la single-day)`);
      console.log("Fixed entries:", items);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la reparare");
    }
  };

  const dedupeEntries = async () => {
    try {
      const res = await api.post("/admin/dedupe-schedule-entries");
      const { duplicates_deleted, entries_merged } = res.data || {};
      if (!duplicates_deleted) {
        toast.info("Nu există duplicate de curățat ✓");
      } else {
        toast.success(`Curățate: ${duplicates_deleted} duplicate, ${entries_merged} entries unite`);
        onChanged?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la curățare duplicate");
    }
  };

  const relinkOrphans = async () => {
    try {
      const res = await api.post("/admin/relink-orphan-entries");
      const { found, linked, linked_regie, linked_project, not_found } = res.data || {};
      if (!found) {
        toast.info("Nu există entries orfane ✓");
      } else if (!linked) {
        toast.warning(`${found} entries orfane găsite dar niciuna legată (nume necunoscute: ${not_found})`);
      } else {
        toast.success(`Legate: ${linked} (${linked_regie} regii + ${linked_project} proiecte)${not_found ? ` · ${not_found} necunoscute` : ""}`);
        onChanged?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    }
  };

  const truncatePastEnd = async () => {
    try {
      const r = await api.post("/admin/truncate-entries-past-end-date");
      const { truncated, deleted } = r.data || {};
      if (!truncated && !deleted) {
        toast.info("Nicio alocare ne-conformă găsită ✓");
      } else {
        toast.success(`✂ ${truncated} alocări trunchiate · ${deleted} șterse (depășeau finalul regiei/proiectului)`);
        onChanged?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    }
  };

  const backfillRates = async () => {
    try {
      const dry = await api.post("/admin/backfill-calendar-rates", { dry_run: true });
      const { candidates_with_zero_rate, updated, skipped_persons_without_rate } = dry.data || {};
      if (!candidates_with_zero_rate) {
        toast.info("Nicio intrare cu tarif 0 ✓");
        return;
      }
      if (!updated) {
        toast.warning(`${candidates_with_zero_rate} intrări cu tarif 0, dar persoanele nu au tarif setat (${skipped_persons_without_rate} sărite). Setează tariful pe persoană mai întâi.`);
        return;
      }
      if (!window.confirm(`Vor fi recalculate ${updated} intrări de calendar (din ${candidates_with_zero_rate}) cu tariful curent al persoanei. Continui?`)) return;
      const res = await api.post("/admin/backfill-calendar-rates", { dry_run: false });
      toast.success(`✓ ${res.data?.updated || 0} intrări recalculate pentru ${res.data?.affected_persons || 0} persoane`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare backfill tarife");
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={backfillSchedule}
        data-testid="admin-backfill-btn"
        title="Extinde alocările vechi single-day până la sfârșitul proiectului/regiei (sâmbăta exclusă)"
      >
        ⚡ Backfill alocări
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={fixSaturday}
        data-testid="admin-fix-saturday-btn"
        title="Repară alocările de sâmbătă care au fost extinse incorect către săptămâna următoare"
        className="border-amber-300 text-amber-700 hover:bg-amber-50"
      >
        🔧 Repară Sâmbete
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={dedupeEntries}
        data-testid="admin-dedupe-btn"
        title="Caută și unește alocările duplicate (același șantier afișat de 2 ori pe aceeași zi)"
      >
        🧹 Curăță duplicate
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
        onClick={relinkOrphans}
        data-testid="admin-relink-btn"
        title="Leagă entries orfane (cu nume dar fără regie/proiect) la regiile/proiectele cu același nume → previne dublarea cardurilor"
      >
        🔗 Leagă orfane
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
        onClick={truncatePastEnd}
        data-testid="admin-truncate-btn"
        title="Trunchează / șterge alocările care depășesc end_date al regiei sau proiectului asociat"
      >
        ✂ Trunchiază depășiri
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
        onClick={backfillRates}
        data-testid="admin-backfill-rates-btn"
        title="Recalculează tariful (CHF/oră) și totalul pentru intrările vechi de calendar care au tarif=0"
      >
        💰 Recalculează tarife
      </Button>
    </>
  );
}
